import { useEffect, useState } from 'react';
import { EventsByResourceType, EventsListType, EventType, ResourceGroupType } from '../SchedulingTypes';
import { COMPACT_MODE_RESOURCE_COUNT, MIN_RESOURCE_ROW_HEIGHT } from '../schedulingConstants';
import { calculateLaneCount, getRenderedEventTimes } from './calculateOverlappingEvents';
import { generateDayKey } from './schedulingDateUtils';

/*
 * useAdjustSchedulerSize.ts
 * A custom hook to use in the scheduling component to maximize
 * the screen height or width.
 */

const useAdjustSchedulerSize = (
    calendarRef: React.RefObject<HTMLDivElement>,
    isTopNavbarEnabled: boolean,
    viewDirection: string,
    viewType: string,
    days: Date[],
    timeSlots: string[] | undefined,
    showInbox: boolean,
    events: EventsListType,
    eventsByResource: EventsByResourceType,
    resourceGroups?: ResourceGroupType[],
    sidePanelWidthPx?: number | null,
) => {
    const [resourceSize, setResourceSize] = useState<number | undefined>(undefined);
    const [isMinimumRowHeight, setIsMinimumRowHeight] = useState<boolean>(false);
    const [scheduleListHeight, setScheduleListHeight] = useState<number>(0);
    const [pixelsPerMinute, setPixelsPerMinute] = useState<number | undefined>(undefined);
    const [dayColumnHeight, setDayColumnHeight] = useState<number | null>(null);
    const [isInitialResizeDone, setIsInitialResizeDone] = useState(false);

    const adjustSchedulerSize = () => {
        // Return if no calendar ref current is known
        if (!calendarRef.current) return;

        // Get the number of visible days
        const daysAmount = days.length;

        // Get the height of the window of the user
        const windowHeight = window.innerHeight;

        // If the top navbar is enabled, get its height
        let topNavbarHeight = 0;
        if (isTopNavbarEnabled) {
            const topNavbar = document.querySelector('.topbar') as HTMLElement;
            topNavbarHeight = topNavbar ? topNavbar.offsetHeight : 0;
        }

        // Get the navbar height
        const navBar = document.querySelector('.navbar') as HTMLElement;
        const subNavBar = document.querySelector('.subnavbar') as HTMLElement;
        const navBarHeight = navBar ? navBar.offsetHeight : 0;
        const subNavBarHeight = subNavBar ? subNavBar.offsetHeight : 0;
    
        // Get the height of the page header and scheduling header
        const navBarWrapper = document.querySelector('.navbar-wrapper') as HTMLElement;
        const navBarWrapperMarginBottom = document.querySelector('.navbar-wrapper') ? parseInt(window.getComputedStyle(navBarWrapper).marginBottom) : 0;
        const pageHeader = document.querySelector('.scheduling-header') as HTMLElement;
        const pageHeaderMarginBottom = pageHeader ? parseInt(window.getComputedStyle(pageHeader).marginBottom) : 0;
        
        // Calculate the height of the page header
        const pageHeaderHeight = pageHeader ? pageHeader.offsetHeight + pageHeaderMarginBottom : 0;

        // Calculate the height of the area above the scheduler component
        const heightAboveScheduler = topNavbarHeight + navBarHeight + subNavBarHeight + navBarWrapperMarginBottom + pageHeaderHeight;

        // Adjust row height for stacked view
        if (viewDirection === 'stacked') {

            /*
             * Set the height per resource row
             */
        
            // Get the height of the board header and the to schedule row
            const boardHeader = document.querySelector('.scheduling-board .header') as HTMLElement;
            const toScheduleRow = document.querySelector('.scheduling-board .unassigned-row') as HTMLElement;
            
            const boardHeaderHeight = boardHeader ? boardHeader.offsetHeight : 0;
            const toScheduleRowHeight = toScheduleRow ? toScheduleRow.offsetHeight : 0;

            // Get the height of the first resource group header row
            const firstResourceGroupHeaderRow = document.querySelector('.scheduling-board .resource-group-header') as HTMLElement;
            const resourceGroupHeaderRowHeight = firstResourceGroupHeaderRow ? (firstResourceGroupHeaderRow.offsetHeight + 1) : 0;

            // Get the number of resource group header rows
            const resourceGroupHeaderRows = document.querySelectorAll('.scheduling-board .resource-group-header');
            const resourceGroupHeaderRowsHeight = resourceGroupHeaderRows.length * resourceGroupHeaderRowHeight;
            
            // Get the number of resource rows
            const resourceRows = document.querySelectorAll('.scheduling-board .adjust-height');
        
            // Calculate the height of resource rows borders (1px per resource row)
            const totalBorderHeight = resourceRows.length * 1;

            // Calculate the height above the resource rows
            const estimatedOtherElementsHeight = heightAboveScheduler + boardHeaderHeight + toScheduleRowHeight + resourceGroupHeaderRowsHeight + totalBorderHeight;
    
            // Calculate the height available for the scheduling rows
            const totalHeightAvailable = windowHeight - estimatedOtherElementsHeight;

            // Set the minimal row height
            const minRowHeight = MIN_RESOURCE_ROW_HEIGHT;

            // Calculate the height per row
            const heightPerRow = Math.max(totalHeightAvailable / resourceRows.length, minRowHeight);

            // Determine if the rows fell back to the minimum height, or if there
            // are so many resources that the rows should stay compact anyway
            const isMinimumHeight = resourceRows.length > 0 && (totalHeightAvailable / resourceRows.length) < minRowHeight;
            const hasManyResources = resourceRows.length >= COMPACT_MODE_RESOURCE_COUNT;
            const isCompactMode = (isMinimumHeight || hasManyResources) && viewType === 'employee_timeline';

            // Share the compact state with the events, they are rendered with a
            // fixed height, without a radius and with vertically centered text
            setIsMinimumRowHeight(isCompactMode);

            // Generate a set of day keys for the visible period, the event
            // state can still hold events of previously visited periods
            const visibleDayKeys = new Set(days.map(day => generateDayKey(day)));

            // Determine the start and end of the visible period in milliseconds
            const periodStartDate = new Date(days[0]);
            periodStartDate.setHours(0, 0, 0, 0);
            const periodEndDate = new Date(days[days.length - 1]);
            periodEndDate.setHours(24, 0, 0, 0);
            const periodStart = periodStartDate.getTime();
            const periodEnd = periodEndDate.getTime();

            // Calculate the highest number of events on one day of a resource,
            // the employee list rows grow with the busiest day of their week
            const calculateMaxEventsPerDay = (resourceId: string): number => {

                // Count the visible events of the resource per day
                const dayEventCounts: Record<string, number> = {};
                (eventsByResource[resourceId] || [])
                    .map(eventId => events[eventId])
                    .filter(event => event !== undefined && !event.deleted && visibleDayKeys.has(event.dayKey))
                    .forEach(event => {
                        dayEventCounts[event.dayKey] = (dayEventCounts[event.dayKey] || 0) + 1;
                    });

                // Return the highest count of all days
                return Object.values(dayEventCounts).reduce((highest, count) => Math.max(highest, count), 0);
            };

            // Calculate the number of stack lanes a resource needs, only the
            // events of the visible period count and their rendered times are
            // used so the height matches the rendered event stacking
            const calculateMaxOverlap = (resourceId: string): number => {

                // Get the events of the resource which fall inside the visible period
                const resourceEvents = (eventsByResource[resourceId] || [])
                    .map(eventId => events[eventId])
                    .filter(event => event !== undefined && !event.deleted && event.startDateTime && event.endDateTime)
                    .filter(event => {
                        const { start, end } = getRenderedEventTimes(event);
                        return start < periodEnd && end > periodStart;
                    });

                // Return the number of lanes needed to stack the events
                return calculateLaneCount(resourceEvents);
            };

            // Devide the available height per row
            if (resourceRows.length > 0){

                // Reset unassigned row width (if set in columns direction adjust width function)
                const unassignedRow = document.querySelector('.scheduling-board .unassigned-row');
                if (unassignedRow) {
                    (unassignedRow as HTMLElement).style.width = 'auto'
                };

                // Update the height of the resource rows
                resourceRows.forEach(row => {
                    const element = row as HTMLElement;

                    // Reset resource rows width (if set in columns direction adjust width function)
                    element.style.width = 'auto';

                    // In compact mode, grow the row with the overlapping events of the
                    // resource so every event keeps the same fixed height and they stack
                    // below each other, instead of shrinking into unreadable slices
                    let rowHeight = heightPerRow;
                    if (isCompactMode && element.dataset.resourceId) {
                        const maxOverlap = calculateMaxOverlap(element.dataset.resourceId);
                        rowHeight = minRowHeight * Math.max(maxOverlap, 1);
                    }

                    // In the employee list, size every row on the busiest day of the
                    // resource plus one extra slot to drag an event into the row
                    if (viewType === 'employee_list' && element.dataset.resourceId) {
                        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
                        const eventSlotHeight = 1.5 * rootFontSize;
                        const dayListPadding = 0.5 * rootFontSize;
                        const maxEventsPerDay = calculateMaxEventsPerDay(element.dataset.resourceId);
                        rowHeight = Math.max((maxEventsPerDay + 1) * eventSlotHeight + dayListPadding, minRowHeight);
                    }

                    // Set the height of the rows
                    element.style.height = `${rowHeight}px`;
                });

                // Mark initial resize as done to make the css visible to prevent flipping effect
                if (!isInitialResizeDone) {
                    setIsInitialResizeDone(true);
                }
            }

            // Set the resource size
            setResourceSize(heightPerRow);

            // Set the schedule list height
            const calendarHeight = windowHeight - heightAboveScheduler - boardHeaderHeight;
            setScheduleListHeight(calendarHeight);


            /*
             * Reset header day columns height
             */

            // Get the header day columns elements
            const headerDayColumns = document.querySelectorAll('.scheduling-board .calendar .header .day-column');

            // Loop through the header day columns and update the height
            if (headerDayColumns.length > 0) {
                headerDayColumns.forEach(row => {
                    const element = row as HTMLElement;

                    // Reset the height of the header day columns to the initial value
                    element.style.height = `2.5rem`;
                });
            }


            /*
             * Reset day columns height to auto
             */

            // Get all day column elements
            const resourceDayColumns = document.querySelectorAll('.scheduling-board .calendar .resources .day-column');

            // Loop through the day columns and update the height
            if (resourceDayColumns.length > 0) {
                resourceDayColumns.forEach(row => {
                    const element = row as HTMLElement;

                    // Reset the height of the day columns
                    element.style.height = `auto`;
                });
            }


            /*
             * Calculate the pixels per minute
             */

            if ((viewType === 'employee_timeline' || viewType === 'project_planner') && timeSlots) {

                // Get the width of the window of the user
                const windowWidth = window.innerWidth;

                // Define the inbox width if shown
                const inboxWidth = showInbox ? (windowWidth * 0.25) : 0;

                // Get the name column width
                const nameColumnWidth = 200;

                // Determine the number of timeslots per day
                const timeSlotsPerDay = timeSlots.length;

                // Calculate the total width of all calendar columns
                const calendarColumnsWidth = windowWidth - nameColumnWidth - inboxWidth;

                // Calculate the width of a single day column
                const dayColumnWidth = calendarColumnsWidth / daysAmount;

                // Calculate the width of a timeslot
                const timeSlotWidth = dayColumnWidth / timeSlotsPerDay;

                // Calculate the width of a minute. Timeslots are 30 minutes.
                const pixelsPerMinute = timeSlotWidth / 30;

                setPixelsPerMinute(pixelsPerMinute);
            }


        // Adjust row width for columns view
        } else if (viewDirection === 'columns') {

            // The compact event styling is only used in the stacked direction
            setIsMinimumRowHeight(false);

            /*
             * Set the width per resource element
             */

            // Get the width of the window of the user
            const windowWidth = window.innerWidth;

            // Define the inbox width if shown, using the actual resized side panel width when known
            const inboxWidth = showInbox ? (sidePanelWidthPx ?? windowWidth * 0.25) : 0;
            
            // Set the width of the day name element (48 px + 1 px border)
            const dayNameWidth = 49;

            // Get the rows to adjustable the width for
            const resourceElements = document.querySelectorAll('.scheduling-board .adjust-width');

            // Calculate the width of resource rows borders (1px per resource row)
            const borderWidth = (resourceElements.length * 1) - 1;

            // Calculate the total width to be used by the rows
            const totalAvailableWidth = windowWidth - inboxWidth - dayNameWidth - borderWidth;

            // Set the minimal element width to 200px
            const minElementWidth = 170;

            // Determine the number of elements which need to scale
            const numberOfElements = resourceElements.length;

            // Calculate the width per element
            const widthPerElement = Math.max(totalAvailableWidth / numberOfElements, minElementWidth);

            // Loop through the resource elements to set the width
            if (resourceElements.length > 0) {
                resourceElements.forEach(resource => {
                    const element = resource as HTMLElement;

                    // Reset resource rows height (if set in stacked direction adjust height function)
                    element.style.height = 'auto';

                    // Set the width of the rows
                    element.style.width = `${widthPerElement}px`;
                });

                // Mark initial resize as done to make the css visible to prevent flipping effect
                if (!isInitialResizeDone) {
                    setIsInitialResizeDone(true);
                } 
            }

            // Set the resource size
            setResourceSize(widthPerElement);

            
            /*
             * Adjust the height of the day rows to fit the screen height
             */

            // Get the height of the first resource group header element
            const resourceGroupHeaderElement = document.querySelector('.scheduling-board .resource-group-header') as HTMLElement;
            const resourceGroupHeaderHeight = resourceGroupHeaderElement ? resourceGroupHeaderElement.offsetHeight : 0;

            // Get the height of the resource name element
            const resourceNameElement = document.querySelector('.scheduling-board .resource-row .name-column') as HTMLElement;
            const resourceNameHeight = resourceNameElement ? resourceNameElement.offsetHeight : 0;

            // Calculate the height of the day borders
            const borderHeights = daysAmount * 1;

            // Calculate the available height for the day rows
            const totalAvailableHeight = windowHeight - heightAboveScheduler - resourceGroupHeaderHeight - resourceNameHeight - borderHeights;

            // Set the minimal row height to 150px
            const minDayHeight = 150;

            // Calculate the height per day column
            const heightPerDay = Math.max(totalAvailableHeight / daysAmount, minDayHeight);

            // Set the day column height
            setDayColumnHeight(heightPerDay);

            // Set the schedule list height
            const calendarHeight = windowHeight - heightAboveScheduler - resourceNameHeight;
            setScheduleListHeight(calendarHeight);    
            

            /*
             * Calculate the pixels per minute
             */

            if (viewType === 'employee_timeline' && timeSlots) {

                // Determine the number of timeslots per day
                const timeSlotsPerDay = timeSlots.length;

                // Calculate the height of a timeslot
                const timeSlotHeight = heightPerDay / timeSlotsPerDay;

                // Calculate the height of a minute. Timeslots are 30 minutes.
                const pixelsPerMinute = timeSlotHeight / 30;

                setPixelsPerMinute(pixelsPerMinute);
            }
        }
    }

    useEffect(() => {
        adjustSchedulerSize();

        const handleResize = () => {
            adjustSchedulerSize();
        };

        window.addEventListener('resize', handleResize);

        // Resize with the nav chrome too, the sub navbar and the notification banner live
        // inside the navbar wrapper and change the height available to the scheduler
        const navBarWrapper = document.querySelector('.navbar-wrapper');
        const navBarResizeObserver = navBarWrapper ? new ResizeObserver(handleResize) : null;
        if (navBarWrapper && navBarResizeObserver) {
            navBarResizeObserver.observe(navBarWrapper);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            navBarResizeObserver?.disconnect();
        };
    }, [calendarRef, isTopNavbarEnabled, viewDirection, days, showInbox, resourceGroups, events, sidePanelWidthPx]);

    return { resourceSize, scheduleListHeight, pixelsPerMinute, dayColumnHeight, isInitialResizeDone, isMinimumRowHeight };
};

export default useAdjustSchedulerSize;